import React, { useRef, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Col, Row } from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { LabelRequiredInput, LabelError } from 'shared/components/Labels';
import DropdownSearch from 'shared/components/DropdownSearch';
import { TextField } from 'shared/components/form/Inputs';
import { Button } from 'shared/components/Buttons';

import { colors } from 'shared/defaultTheme';
import { cleanSpaceString } from 'shared/utils';

const Form = ({ defaultValue, onCancel, onSubmit }) => {
  const { t } = useTranslation();
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const [stateColors] = useState([
    { id: '#C43741', name: 'Rojo', color: '#C43741' },
    { id: '#E5869D', name: 'Rojo claro', color: '#E5869D' },
    { id: '#F1923E', name: 'Naranja', color: '#F1923E' },
    { id: '#F1BC3E', name: 'Amarillo', color: '#F1BC3E' },
    { id: '#5EC6DD', name: 'Azul claro', color: '#5EC6DD' },
    { id: '#7683B5', name: 'Azul oscuro', color: '#7683B5' },
    { id: '#77D166', name: 'Verde', color: '#77D166' },
    { id: '#7A58AC', name: 'Morado', color: '#7A58AC' },
    { id: '#929B9C', name: 'Gris', color: '#929B9C' },
    { id: '#9A7E74', name: 'Marrón', color: '#9A7E74' },
  ]);
  const newStateColor = useRef(null);
  const [color, setColor] = useState(null);
  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: defaultValue
      ? {
          stateName: defaultValue.name,
          stateColor: defaultValue.color,
        }
      : undefined,
  });
  newStateColor.current = watch('stateColor', '');

  useEffect(() => {
    reset({
      stateColor: defaultValue?.color,
    });
  }, [defaultValue]);

  const getNameSelectedLanguage = (i18List) => {
    if (i18List) {
      const findName = i18List.find((x) => x.i18Code === selectedDefaultLanguage);
      if (findName && findName?.i18Name !== '') {
        return findName?.i18Name;
      }
      return i18List[0].i18Name;
    }
    return null;
  };

  useEffect(() => {
    if (defaultValue) {
      setValue('stateName', getNameSelectedLanguage(defaultValue.i18List));
    }
  }, [defaultValue]);

  useEffect(() => {
    const getColor = (value) => {
      setColor(value);
    };
    if (newStateColor.current) {
      getColor(newStateColor.current);
    }
  }, [newStateColor.current]);

  return (
    <Row className="row-content w-100">
      <Col sm={12}>
        <form className="form form-field" noValidate onSubmit={handleSubmit(onSubmit)}>
          <Row className="w-100">
            <Col sm={12}>
              <div className="form__form-group">
                <LabelRequiredInput
                  className="form__form-group-label"
                  label={t('state_text_name')}
                  color={colors.black}
                  error={errors.stateName !== undefined}
                />
                <Controller
                  name="stateName"
                  control={control}
                  defaultValue=""
                  rules={{ required: { value: true, message: t('general_required_field') } }}
                  render={({ field }) => (
                    <TextField
                      value={field.value}
                      onChange={(e) => {
                        const newEvent = cleanSpaceString(e);
                        field.onChange(newEvent);
                      }}
                      onBlur={field.onBlur}
                      error={errors.stateName !== undefined}
                    />
                  )}
                />
                {errors.stateName && <LabelError label={errors.stateName.message} />}
              </div>
            </Col>
          </Row>
          <Row className="w-100">
            <Col sm={12}>
              <div className="form__form-group">
                <LabelRequiredInput
                  className="form__form-group-label"
                  label={t('state_text_color')}
                  color={colors.black}
                  error={errors.stateColor !== undefined}
                />
                <Controller
                  name="stateColor"
                  control={control}
                  defaultValue=""
                  rules={{ required: { value: true, message: t('general_required_field') } }}
                  render={({ field }) => (
                    <>
                      {stateColors ? (
                        <DropdownSearch
                          id="state-color-dropdown-search"
                          options={stateColors.map((item) => ({
                            name: item.name,
                            value: item.id,
                            color: item.color,
                          }))}
                          value={field.value}
                          onBlur={field.onBlur}
                          onChange={field.onChange}
                          error={errors.stateColor !== undefined}
                          isColor
                          color={color}
                        />
                      ) : null}
                    </>
                  )}
                />
                {errors.stateColor && <LabelError label={errors.stateColor.message} />}
              </div>
            </Col>
          </Row>
          <Row className="w-100">
            <Col className="d-flex flex-row justify-content-end" sm={12}>
              <Button
                className="text-uppercase mr-4"
                type="button"
                color="secondary"
                label={t('general_button_cancel')}
                onClick={onCancel}
              />
              <Button
                className="text-uppercase"
                type="submit"
                color="primary"
                label={t(defaultValue ? 'general_button_update' : 'general_add')}
              />
            </Col>
          </Row>
        </form>
      </Col>
    </Row>
  );
};

Form.defaultProps = {
  defaultValue: undefined,
};

Form.propTypes = {
  defaultValue: PropTypes.shape({
    name: PropTypes.string,
    color: PropTypes.string,
    i18List: PropTypes.arrayOf(PropTypes.shape()),
  }),
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default Form;
